/**
 * Floating Farsi (Persian) keyboard - ISIRI 9147-style layout.
 * One row per line, no wrapping. RTL: first key in each row array is rightmost.
 * Shift toggles the second level (harakat, punctuation, alternate letters).
 */

import React, { useState } from 'react';
import { CornerDownLeft } from 'lucide-react';

// Arrays in RTL order: first = rightmost key.
const ROW_NUM = ['۰', '۹', '۸', '۷', '۶', '۵', '۴', '۳', '۲', '۱'];
const ROW1 = ['چ', 'ج', 'ح', 'خ', 'ه', 'ع', 'غ', 'ف', 'ق', 'ث', 'ص', 'ض'];
const ROW2 = ['گ', 'ک', 'م', 'ن', 'ت', 'ا', 'ل', 'ب', 'ی', 'س', 'ش'];
const ROW3 = ['و', 'پ', 'د', 'ذ', 'ر', 'ز', 'ط', 'ظ'];
const ZWNJ = '\u200C';
const ZWJ = '\u200D';
const ROW4 = ['ژ', ZWNJ, ' ', 'آ'];

/** ISIRI 9147 second level (Shift), same RTL order as the base rows. */
const SHIFT_ROW_NUM = ['(', ')', '*', '،', '×', '٪', '﷼', '٫', '٬', '!'];
const SHIFT_ROW1 = ['{', '}', '[', ']', 'ّ', 'َ', 'ِ', 'ُ', 'ً', 'ٍ', 'ٌ', 'ْ'];
const SHIFT_ROW2 = ['؛', ':', '«', '»', 'ة', 'آ', 'أ', 'إ', 'ي', 'ئ', 'ؤ'];
const SHIFT_ROW3 = ['>', 'ء', 'ٔ', ZWNJ, 'ٰ', 'ژ', 'ٓ', 'ك'];
/** Useful extras on bottom row while Shift is on. */
const SHIFT_ROW4 = ['؟', ZWJ, ' ', 'ـ'];

const COMBINING_MARKS = new Set([
  'َ',
  'ِ',
  'ُ',
  'ً',
  'ٍ',
  'ٌ',
  'ْ',
  'ّ',
  'ٰ',
  'ٓ',
  'ٔ',
  'ٕ',
]);

const spaceKeyLabelClass = 'text-[10px] font-medium leading-none whitespace-nowrap';

function keyDisplayLabel(char: string): React.ReactNode {
  if (char === ' ') {
    return (
      <span dir="ltr" className={spaceKeyLabelClass}>
        Space
      </span>
    );
  }
  if (char === ZWNJ) {
    return (
      <span dir="ltr" className={spaceKeyLabelClass}>
        Semi-Space
      </span>
    );
  }
  if (char === ZWJ) {
    return (
      <span dir="ltr" className={spaceKeyLabelClass}>
        ZWJ
      </span>
    );
  }
  // Dotted circle so harakat/diacritics are visible on the keycap.
  if (COMBINING_MARKS.has(char)) {
    return `\u25CC${char}`;
  }
  return char;
}

export interface FarsiKeyboardProps {
  onInsert: (char: string) => void;
  onBackspace: () => void;
  /** When set, shows an Enter key (right of ژ) that dismisses the keyboard. */
  onClose?: () => void;
  className?: string;
  dir?: 'rtl' | 'ltr';
}

function KeyRow({
  keys,
  onInsert,
  onBackspace,
  showBackspace = false,
  showEnter = false,
  onEnter,
  showShift = false,
  shiftActive = false,
  onToggleShift,
}: {
  keys: string[];
  onInsert: (char: string) => void;
  onBackspace: () => void;
  showBackspace?: boolean;
  showEnter?: boolean;
  onEnter?: () => void;
  showShift?: boolean;
  shiftActive?: boolean;
  onToggleShift?: () => void;
}) {
  const handleKeyDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const keyClass =
    'h-9 min-w-0 flex-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-medium text-base hover:bg-gray-200 dark:hover:bg-gray-600 active:scale-95 transition-transform select-none flex items-center justify-center';

  return (
    <div className="flex gap-0.5 flex-nowrap w-full" dir="rtl">
      {showBackspace && (
        <button
          type="button"
          onMouseDown={handleKeyDown}
          onClick={onBackspace}
          className="h-9 px-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 font-medium text-xs hover:bg-amber-200 dark:hover:bg-amber-800/40 active:scale-95 transition-transform select-none flex-shrink-0"
        >
          ⌫
        </button>
      )}
      {showEnter && onEnter && (
        <button
          type="button"
          onMouseDown={handleKeyDown}
          onClick={onEnter}
          className="h-9 px-2.5 rounded border border-emerald-300 dark:border-emerald-700 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 font-medium text-xs hover:bg-emerald-200 dark:hover:bg-emerald-800/40 active:scale-95 transition-transform select-none flex-shrink-0 flex items-center justify-center gap-1"
          aria-label="Enter"
        >
          <CornerDownLeft className="w-4 h-4" />
          <span>Enter</span>
        </button>
      )}
      {keys.map((char, idx) => {
        const isSpace = char === ' ';
        const isZwnj = char === ZWNJ;
        const isZwj = char === ZWJ;
        const keyId = isSpace
          ? 'space'
          : isZwnj
            ? 'zwnj'
            : isZwj
              ? 'zwj'
              : `${char}-${idx}`;
        const keyButtonClass = isSpace
          ? `${keyClass} flex-[4] min-w-[4.5rem]`
          : isZwnj || isZwj
            ? `${keyClass} flex-none min-w-[4rem] px-0.5`
            : keyClass;

        return (
          <button
            key={keyId}
            type="button"
            onMouseDown={handleKeyDown}
            onClick={() => onInsert(char)}
            className={keyButtonClass}
            aria-label={
              isZwnj ? 'Semi-Space' : isZwj ? 'Zero-width joiner' : isSpace ? 'Space' : undefined
            }
          >
            {keyDisplayLabel(char)}
          </button>
        );
      })}
      {showShift && onToggleShift && (
        <button
          type="button"
          onMouseDown={handleKeyDown}
          onClick={onToggleShift}
          aria-pressed={shiftActive}
          aria-label="Shift"
          title="Shift"
          className={`h-9 min-w-[2.75rem] px-2 rounded border font-medium text-xs active:scale-95 transition-transform select-none flex-shrink-0 flex items-center justify-center ${
            shiftActive
              ? 'border-blue-500 dark:border-blue-400 bg-blue-600 text-white hover:bg-blue-700'
              : 'border-gray-400 dark:border-gray-500 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-500'
          }`}
        >
          ⇧ Shift
        </button>
      )}
    </div>
  );
}

export const FarsiKeyboard: React.FC<FarsiKeyboardProps> = ({
  onInsert,
  onBackspace,
  onClose,
  className = '',
}) => {
  const [shift, setShift] = useState(false);

  const rows = shift
    ? [SHIFT_ROW_NUM, SHIFT_ROW1, SHIFT_ROW2, SHIFT_ROW3, SHIFT_ROW4]
    : [ROW_NUM, ROW1, ROW2, ROW3, ROW4];

  const toggleShift = () => setShift((v) => !v);

  return (
    <div
      className={`farsi-keyboard bg-gray-100 dark:bg-gray-800 rounded-lg p-1.5 shadow-lg border border-gray-200 dark:border-gray-600 ${className}`}
      dir="rtl"
    >
      <div className="flex flex-col gap-1 w-full max-w-full overflow-hidden">
        <KeyRow keys={rows[0]} onInsert={onInsert} onBackspace={onBackspace} />
        <KeyRow keys={rows[1]} onInsert={onInsert} onBackspace={onBackspace} />
        <KeyRow keys={rows[2]} onInsert={onInsert} onBackspace={onBackspace} />
        <KeyRow
          keys={rows[3]}
          onInsert={onInsert}
          onBackspace={onBackspace}
          showShift
          shiftActive={shift}
          onToggleShift={toggleShift}
        />
        <KeyRow
          keys={rows[4]}
          onInsert={onInsert}
          onBackspace={onBackspace}
          showBackspace
          showEnter={!!onClose}
          onEnter={onClose}
        />
      </div>
    </div>
  );
};

export default FarsiKeyboard;
